import { Flex, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { customerUserRoleLabel } from '@texas/utils/helpers/roleHelpers';
import { customersDirectoryStyles } from '@texas/resources/theme/styles/customersDirectory';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchivedTooltip } from '../../shared/tooltip/ArchivedTooltip';
import { CustomerNameCell } from '../CustomerNameCell';
import {
  CustomerDirectoryListItem,
  CustomerType,
} from '@texas/api/endpoints/customersApi';

const columnHelper = createColumnHelper<CustomerDirectoryListItem>();

export function useCustomersColumns() {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => (
          <CustomerNameCell
            id={info.row.original.id}
            name={info.getValue()}
            logoIdentifier={info.row.original.logoIdentifier}
          />
        ),
        header: t('general.brand'),
        size: 300,
        minSize: 240,
      }),
      columnHelper.accessor('customerType', {
        cell: (info) =>
          info.getValue() === CustomerType.Vendor
            ? t('general.vendor')
            : t('general.brand'),
        header: t('general.type'),
        size: 105,
        minSize: 95,
      }),
      columnHelper.accessor('erpId', {
        cell: (info) => (
          <Text sx={customersDirectoryStyles.emphasis}>
            {info.getValue() ?? '—'}
          </Text>
        ),
        header: t('brandsDirectory.brandNumber'),
        size: 120,
        minSize: 110,
      }),
      columnHelper.accessor('project', {
        cell: (info) => info.getValue() ?? '—',
        header: t('general.project'),
        size: 150,
        minSize: 130,
      }),
      columnHelper.accessor('articleCount', {
        cell: (info) => (
          <Text sx={customersDirectoryStyles.emphasis}>{info.getValue()}</Text>
        ),
        header: t('general.articles'),
        size: 180,
        minSize: 120,
      }),
      columnHelper.accessor('currentUserRoles', {
        cell: (info) =>
          customerUserRoleLabel(
            info.getValue(),
            info.row.original.currentUserCustomRoles,
            t,
          ) ?? '—',
        header: t('brandsDirectory.myRole'),
        enableSorting: false,
        size: 190,
        minSize: 150,
      }),
      columnHelper.accessor('id', {
        cell: (info) => (
          <Flex sx={customersDirectoryStyles.statusCell}>
            {info.row.original.archived && (
              <ArchivedTooltip archived={info.row.original.archived} />
            )}
          </Flex>
        ),
        header: '',
        enableSorting: false,
        size: 48,
        maxSize: 48,
      }),
    ],
    [t],
  );
}
